import { cn } from '@/lib/infra/utils';
import {
  resultSeverity,
  type OrderResultLine,
  type OrderResultSeverity,
} from './oscarResultPresentation';

/**
 * #474 — the order ticket's single result slot.
 *
 * Presentation only: it renders the line it is handed and classifies nothing.
 * Every Kalshi, Polymarket and Oscar outcome in the ticket comes through here,
 * so the tone scale and the announcement rules are decided in one place.
 *
 * The tones match the durable Overtake receipt below it (see the state classes
 * in OscarSequenceReceipt) — the transient line and the audit card must not
 * disagree about how bad the same sequence was.
 */

/** Exported for the Polymarket US receipt line (#1247), which takes the same tones. */
export const ORDER_RESULT_SEVERITY_CLASS: Record<OrderResultSeverity, string> = {
  success: 'bg-positive/10 text-positive',
  warning: 'bg-amber-500/10 text-amber-500',
  critical: 'bg-negative/10 text-negative',
  // Neutral: the same muted pill the one-click ack strip and the receipt
  // rows use for a not-yet-settled state. Never green before the venue speaks.
  pending: 'bg-muted text-muted-foreground',
};

export interface OrderResultBannerProps {
  result: OrderResultLine | null;
}

export function OrderResultBanner({ result }: OrderResultBannerProps) {
  if (!result) return null;
  const severity = resultSeverity(result);
  // A success waits its turn; so does the in-flight line — the trader just
  // pressed the button and does not need to be interrupted about it.
  const polite = severity === 'success' || severity === 'pending';
  return (
    <div
      data-testid="order-result"
      data-severity={severity}
      // Absent on a plain placement — only Oscar names a wording class.
      data-kind={result.kind}
      // A rejection or an exposed level interrupts; success and pending wait.
      role={polite ? 'status' : 'alert'}
      {...(polite ? { 'aria-live': 'polite' as const } : {})}
      {...(result.reference ? { title: result.reference } : {})}
      // Contained: the line lives in a fixed-width rail beside the fire button
      // and the durable receipt. A long venue reason or an unbroken raw ticker
      // wraps INSIDE it (`overflow-wrap: anywhere` breaks even a token with no
      // break opportunity) rather than widening the card or compressing the
      // controls around it. `min-w-0` lets a flex/grid parent shrink it.
      className={cn(
        'min-w-0 max-w-full whitespace-normal [overflow-wrap:anywhere] rounded-md px-2.5 py-1.5 text-xs',
        ORDER_RESULT_SEVERITY_CLASS[severity],
      )}
    >
      {result.msg}
    </div>
  );
}
